import { YES_PLACEHOLDER_NODE_ID_PREFIX } from '../flowsNode/BranchingNode';

/**
 * Helper function to check if inserting edge would create a cycle
 */
const wouldCreateCycle = (sourceId, targetId, nodes) => {
    // If source and target are the same, that's a direct cycle
    if (sourceId === targetId) return true;
    
    // Helper function for DFS traversal
    const hasPathDFS = (currentId, visited = new Set()) => {
        // If we've reached the source, we found a cycle
        if (currentId === sourceId) return true;
        
        // Mark current node as visited
        visited.add(currentId);
        
        // Get all children of current node
        const currentNode = nodes.find(n => n.id === currentId);
        if (!currentNode || !currentNode.data.children) return false;
        
        // Check each child
        for (const childId of currentNode.data.children) {
            if (!visited.has(childId) && hasPathDFS(childId, visited)) {
                return true;
            }
        }
        
        return false;
    };
    
    // Start DFS from target node
    return hasPathDFS(targetId);
};

/**
 * Helper function to create edges between nodes
 * @private
 */
const createEdges = (sourceId, targetId, options = {}) => {
    const { 
        label = null, 
        style = {}, 
        type = 'smoothstep' 
    } = options;
    
    return {
        id: `${sourceId}-${targetId}${label ? `-${label.toLowerCase()}` : ''}`,
        source: sourceId,
        target: targetId,
        type,
        ...(label && { label }),
        ...(Object.keys(style).length > 0 && { style })
    };
};

/**
 * Finds and removes an edge between specified nodes
 * @private
 */
const findRemoveEdge = (sourceId, targetId, edges) => {
    console.log('🔴 FINDING REMOVE EDGE', {
        sourceId,
        targetId,
        edges
    });
    const closestEdge = edges.find(e => 
        e.source === sourceId && e.target === targetId);
    
    if (!closestEdge) return { found: false, newEdges: edges };
    
    const newEdges = edges.filter(e => e.id !== closestEdge.id);
    return { found: true, newEdges, removedEdge: closestEdge };
};

/**
 * Function to insert a flow node between a source and target node
 */
const insertFlowNode = (draggedNode, sourceNode, targetNode, nodes, edges, setNodes, setEdges) => {
    // Remove the existing edge
    const { found, newEdges } = findRemoveEdge(sourceNode.id, targetNode.id, edges);
    
    if (!found) return nodes;
    
    // Create two new edges: source -> draggedNode and draggedNode -> target
    const edge1 = createEdges(sourceNode.id, draggedNode.id);
    const edge2 = createEdges(draggedNode.id, targetNode.id);
    
    // Update edges
    setEdges([...newEdges, edge1, edge2]);
    
    // Update the nodes with new parent/child relationships
    return nodes.map(n => {
        if (n.id === sourceNode.id) {
            // Update source node's children: replace targetNode.id with draggedNode.id
            const updatedChildren = n.data.children 
                ? n.data.children.map(id => id === targetNode.id ? draggedNode.id : id)
                : [draggedNode.id];
                
            return {
                ...n,
                data: {
                    ...n.data,
                    children: updatedChildren
                }
            };
        }
        else if (n.id === targetNode.id) {
            // Update target node's parent: replace sourceNode.id with draggedNode.id
            return {
                ...n,
                data: {
                    ...n.data,
                    parent: draggedNode.id
                }
            };
        }
        else if (n.id === draggedNode.id) {
            // Update dragged node with new parent and children
            return {
                ...n,
                data: {
                    ...n.data,
                    parent: sourceNode.id,
                    children: n.data.children 
                        ? [...n.data.children, targetNode.id]
                        : [targetNode.id]
                }
            };
        }
        return n;
    });
};

/**
 * Inserts will always happend between two nodes: Node -> (insert) -> Node or Node -> (insert) -> endNode
 * Branching insertion algo: 
 * 1. Find the targetNode
 * 2. Replace current Yes Placeholder node with targetNode
 * 
 */
const insertBranchingNode = (draggedNode, sourceNode, targetNode, nodes, edges, setNodes, setEdges) => {
    // Add visible log for debugging
    console.log('🔴 BRANCHING NODE INSERTION STARTED', {
        draggedNodeId: draggedNode.id,
        draggedNodeType: draggedNode.data.nodeType,
        sourceNodeId: sourceNode.id,
        targetNodeId: targetNode.id
    });
    
    // Remove the existing edge
    const result = findRemoveEdge(sourceNode.id, targetNode.id, edges);
    const found = result.found;
    let newEdges = result.newEdges;
    
    if (!found) {
        console.log('🔴 BRANCHING NODE INSERTION ABORTED: No matching edge found');
        return nodes;
    }

    // Remove Yes Placeholder Node If It Exists
    const yesPlaceholderId = draggedNode.data.children.find(id => id.startsWith(YES_PLACEHOLDER_NODE_ID_PREFIX));
    let newNodes = nodes;
    if (yesPlaceholderId) { 
        console.log('🔴 YES PLACEHOLDER ID', yesPlaceholderId);
        console.log('🔴 NODES BEFORE FILTER', nodes.map(n => n.id));
        newNodes = nodes.filter(n => n.id !== yesPlaceholderId);
        console.log('🔴 NODES AFTER FILTER', newNodes.map(n => n.id));
        const result = findRemoveEdge(draggedNode.id, yesPlaceholderId, edges);
        newEdges = result.newEdges;

        if (!result.found) {
            console.log('🔴 BRANCHING NODE INSERTION ABORTED: No matching edge found'); 
            console.log('🔴 YES PLACEHOLDER ID', yesPlaceholderId);
            console.log('🔴 NODES', nodes);
            console.log('🔴 EDGES', edges);
            return nodes;
        }
    }

    // Create Yes, No Branch Edge
    const sourceToDraggedNodeEdge = createEdges(sourceNode.id, draggedNode.id);
    const branchToTargetEdge = createEdges(draggedNode.id, targetNode.id, {
        label: 'Yes',
        style: { stroke: '#00AA00' }
    });

    // Update edges array with new edges
    setNodes(newNodes);
    setEdges([...newEdges, sourceToDraggedNodeEdge, branchToTargetEdge]);
    
    // Update the nodes with branching-specific parent/child relationships
    const updatedNodes = newNodes.map(n => {
        if (n.id === sourceNode.id) {
            // Update source node's children: replace targetNode.id with draggedNode.id
            const updatedChildren = n.data.children 
                ? n.data.children.filter(id => id !== targetNode.id).concat([draggedNode.id])
                : [draggedNode.id];
                
            return {
                ...n,
                data: {
                    ...n.data,
                    children: updatedChildren
                }
            };
        }
        else if (n.id === targetNode.id) {
            // For branching, we need to specify which branch this target belongs to
            return {
                ...n,
                data: {
                    ...n.data,
                    parent: draggedNode.id,
                }
            };
        }
        else if (n.id === draggedNode.id) {
            return {
                ...n,
                data: {
                    ...n.data,
                    parent: sourceNode.id,
                    children: n.data.children 
                        ? [...n.data.children, targetNode.id]
                        : [targetNode.id]
                }
            };
        }
        return n;
    });
    
    // Add the no placeholder node to the updated nodes
    const finalNodes = [...updatedNodes];
    
    console.log('🔴 BRANCHING NODE INSERTION COMPLETED', {
        updatedBranchingNode: finalNodes.find(n => n.id === draggedNode.id),
    });


    console.log('🔴 NODES BABY', nodes);
    
    return finalNodes;
};

/**
 * Function to check if a node is close to an edge and insert it if needed
 */
const checkAndInsertNode = (draggedNode, nodes, edges, setNodes, setEdges) => {
    console.log('🔵 CHECK AND INSERT START', {
        draggedNodeId: draggedNode.id,
        nodeType: draggedNode.data.nodeType
    });
    
    // Check if the node already has existing connections
    const hasExistingConnections = 
        (draggedNode.data.parent !== undefined && draggedNode.data.parent !== null) || 
        (draggedNode.data.children && draggedNode.data.children.length > 2);
        
    // Skip insertion if the node already has connections
    if (hasExistingConnections) {
        console.log('🔵 INSERTION SKIPPED: Node has existing connections');
        return;
    }
    
    // Distance threshold for considering a node close to an edge
    const thresholdDistance = 50;
    
    // Loop through all edges to find the closest one
    let closestEdge = null;
    let minDistance = Infinity;
    
    edges.forEach(edge => {
        if (edge.source === draggedNode.id || edge.target === draggedNode.id) {
            return;
        }
        
        // Find source and target nodes of the edge
        const sourceNode = nodes.find(n => n.id === edge.source);
        const targetNode = nodes.find(n => n.id === edge.target);
        
        if (sourceNode && targetNode) {
            // Calculate midpoint of the edge
            const midX = (sourceNode.position.x + targetNode.position.x) / 2;
            const midY = (sourceNode.position.y + targetNode.position.y) / 2;
            
            // Calculate distance from dragged node to midpoint
            const distance = Math.sqrt(
                Math.pow(draggedNode.position.x - midX, 2) + 
                Math.pow(draggedNode.position.y - midY, 2)
            );
            
            if (distance < minDistance && distance < thresholdDistance) {
                minDistance = distance;
                closestEdge = edge;
            }
        }
    });
    
    // If a close edge is found, insert the node between the source and target
    if (closestEdge) {
        // Check if this insertion would create a cycle
        if (wouldCreateCycle(closestEdge.source, draggedNode.id, nodes) || 
            wouldCreateCycle(draggedNode.id, closestEdge.target, nodes)) {
            // Skip this insertion to prevent cycle
            console.log('🔵 INSERTION SKIPPED: Would create cycle');
            return;
        }
        
        // Find source and target nodes
        const sourceNode = nodes.find(n => n.id === closestEdge.source);
        const targetNode = nodes.find(n => n.id === closestEdge.target);
        
        if (!sourceNode || !targetNode) {
            console.log('🔵 INSERTION SKIPPED: Source or target node not found');
            return;
        }
        
        // Determine node type and call appropriate insert method
        let updatedNodes;
        
        // Check the node type and call the appropriate insertion method
        if (draggedNode.type === 'branchingNode') {
            console.log('🔵 DETECTED BRANCHING NODE TYPE', {
                nodeType: draggedNode.data.nodeType,
                nodeData: draggedNode.data
            });
            updatedNodes = insertBranchingNode(draggedNode, sourceNode, targetNode, nodes, edges, setNodes, setEdges);
        } else {
            console.log('🔵 DETECTED REGULAR FLOW NODE TYPE', {
                nodeType: draggedNode.data.nodeType || 'flowNode'
            });
            // Default to flowNode handling
            updatedNodes = insertFlowNode(draggedNode, sourceNode, targetNode, nodes, edges, setNodes, setEdges);
        }
        
        // Update nodes with new relationships
        setNodes(updatedNodes);
        console.log('🔵 NODE INSERTION COMPLETED');
    } else {
        console.log('🔵 NO CLOSE EDGE FOUND');
    }
};

export default checkAndInsertNode;
